import { Client, Frame, IFrame, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { IIncident } from '../../shared/interfaces/incident/IIncident';

export type WebSocketMessageHandler = (message: IMessage) => void;

class WebSocketService {
  private stompClient: Client | null = null;
  private isConnecting: boolean = false;
  private subscriptions: Map<string, any> = new Map(); // topic -> subscription
  private messageHandlers: Map<string, WebSocketMessageHandler[]> = new Map();
  private connectionPromise: Promise<void> | null = null;

  /**
   * Connect to WebSocket server using SockJS
   */
  connect(wsUrl?: string): Promise<void> {
    // Default URL based on environment
    const defaultWsUrl = process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_WS_DEV_MODE || 'http://localhost:8082/ws-stomp'
      : process.env.REACT_APP_WS_PRO_MODE || 'https://api.safesignal.at/ws-stomp';
    
    const finalWsUrl = wsUrl || defaultWsUrl;
    console.log('[WebSocket] Attempting to connect to:', finalWsUrl);
    
    if (this.stompClient?.connected) {
      console.log('[WebSocket] Already connected, returning resolved promise');
      return Promise.resolve();
    }

    if (this.connectionPromise) {
      console.log('[WebSocket] Connection in progress, returning existing promise');
      return this.connectionPromise;
    }

    if (this.isConnecting) {
      console.log('[WebSocket] Connection already initiated, waiting for completion');
      return new Promise((resolve) => {
        const checkConnection = setInterval(() => {
          if (this.stompClient?.connected) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100);
      });
    }

    this.isConnecting = true;
    console.log('[WebSocket] Starting new connection with SockJS...');

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.stompClient = new Client({
          // Use SockJS as the WebSocket factory
          webSocketFactory: () => new SockJS(finalWsUrl) as any,
          reconnectDelay: 5000,
          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000,
          debug: (msg: string) => {
            if (process.env.NODE_ENV === 'development') {
              console.log('[STOMP Debug]', msg);
            }
          },
          onConnect: (frame: Frame) => {
            console.log('✅ [WebSocket] Connected successfully!', frame);
            this.isConnecting = false;
            this.restoreSubscriptions();
            resolve();
          },
          onStompError: (frame: Frame) => {
            console.error('❌ [WebSocket] STOMP error:', frame);
            this.isConnecting = false;
            this.connectionPromise = null;
            reject(frame);
          },
          onWebSocketError: (event: any) => {
            console.error('❌ [WebSocket] WebSocket error:', event);
          },
          onDisconnect: (frame: Frame) => {
            console.log('[WebSocket] Disconnected:', frame);
            this.connectionPromise = null;
          },
        });

        console.log('[WebSocket] Client created with SockJS, activating...');
        this.stompClient.activate();
      } catch (error) {
        console.error('❌ [WebSocket] Failed to create connection:', error);
        this.isConnecting = false;
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    console.log('[WebSocket] Disconnecting... Active subscriptions:', this.subscriptions.size);
    
    if (this.stompClient?.connected) {
      // Unsubscribe from all topics
      this.subscriptions.forEach((subscription, topic) => {
        console.log(`[WebSocket] Unsubscribing from: ${topic}`);
        if (subscription) {
          subscription.unsubscribe();
        }
      });
      this.subscriptions.clear();
      this.messageHandlers.clear();

      this.stompClient.deactivate().then(() => {
        console.log('✅ [WebSocket] Disconnected successfully');
        this.stompClient = null;
      });
    } else {
      console.warn('[WebSocket] Cannot disconnect - not connected');
    }
  }

  /**
   * STOMP subscriptions die with the connection, so after an automatic
   * reconnect every topic that still has handlers must be subscribed again.
   */
  private restoreSubscriptions(): void {
    this.subscriptions.clear();
    this.messageHandlers.forEach((handlers, topic) => {
      if (handlers.length > 0) {
        this.subscriptions.set(topic, this.createSubscription(topic));
      }
    });
  }

  private createSubscription(topic: string) {
    return this.stompClient!.subscribe(topic, (message: IMessage) => {
      console.log(`📨 [WebSocket] Message received on ${topic}:`, message.body);
      const handlers = this.messageHandlers.get(topic) || [];
      handlers.forEach((h) => h(message));
    });
  }

  /**
   * Subscribe to a topic and handle messages
   */
  subscribe(
    topic: string,
    handler: WebSocketMessageHandler,
  ): () => void {
    console.log(`[WebSocket] Subscribe request for topic: ${topic}`);
    
    if (!this.stompClient?.connected) {
      console.warn(
        `⚠️ [WebSocket] Not connected! Cannot subscribe to ${topic}. Current state:`,
        { connected: this.stompClient?.connected, isConnecting: this.isConnecting }
      );
      return () => {};
    }

    // Add handler to list
    if (!this.messageHandlers.has(topic)) {
      this.messageHandlers.set(topic, []);
    }
    this.messageHandlers.get(topic)!.push(handler);
    console.log(`[WebSocket] Handler added for ${topic}. Total handlers: ${this.messageHandlers.get(topic)?.length}`);

    // Subscribe if not already subscribed
    if (!this.subscriptions.has(topic)) {
      console.log(`[WebSocket] Creating new subscription for: ${topic}`);
      const subscription = this.createSubscription(topic);

      this.subscriptions.set(topic, subscription);
      console.log(`✅ [WebSocket] Successfully subscribed to ${topic}`);
      console.log('[WebSocket] Total active subscriptions:', this.subscriptions.size);
    } else {
      console.log(`[WebSocket] Already subscribed to ${topic}, adding additional handler`);
    }

    // Return unsubscribe function
    return () => {
      console.log(`[WebSocket] Unsubscribe request for topic: ${topic}`);
      const handlers = this.messageHandlers.get(topic) || [];
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
        console.log(`[WebSocket] Handler removed from ${topic}. Remaining handlers: ${handlers.length}`);
      }

      // If no more handlers, unsubscribe from topic
      if (handlers.length === 0) {
        const subscription = this.subscriptions.get(topic);
        if (subscription) {
          subscription.unsubscribe();
          this.subscriptions.delete(topic);
          this.messageHandlers.delete(topic);
          console.log(`✅ [WebSocket] Unsubscribed from ${topic}`);
          console.log('[WebSocket] Total active subscriptions:', this.subscriptions.size);
        }
      }
    };
  }

  /**
   * Send a message to a topic (if supported by backend)
   */
  send(destination: string, message: any): void {
    console.log(`[WebSocket] Send request to ${destination}:`, message);
    
    if (!this.stompClient?.connected) {
      console.warn(`❌ [WebSocket] Cannot send - not connected to ${destination}`);
      return;
    }

    try {
      this.stompClient.publish({
        destination,
        body: JSON.stringify(message),
      });
      console.log(`✅ [WebSocket] Message sent to ${destination}`);
    } catch (error) {
      console.error(`❌ [WebSocket] Error sending to ${destination}:`, error);
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    const connected = this.stompClient?.connected ?? false;
    console.log(`[WebSocket] isConnected check:`, connected);
    return connected;
  }
}

export const websocketService = new WebSocketService();
